import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { canEditSelectedProfile, useUserStore } from "../../../store/user";
import { homeService } from "../services";
import { Sidebar } from "../../../components/sidebar";
import { EditProfileModal, type Profile } from "../../profiles-tracking/components/edit-profile-modal";
import { toast } from "react-toastify";
import { profilesTrackingService } from "../../profiles-tracking/services";
import { useEditProfileModal } from "../../../contexts/useEditProfileModal";
import { PaymentSuccessModal } from "../components/payment-success-modal";
import { ReportsService } from "../../reports/services";
import type { KeywordResponse } from "../../reports/types";
import { profileIdDoUsuario, reviewsService } from "../../reviews/services";
import { itensDeCompletude } from "../../profiles-tracking/components/edit-profile-modal/components/editor-chrome-helpers";
import { Badge, Botao, BotaoPequeno, Hint } from "../../profiles-tracking/components/edit-profile-modal/components/EditorChrome";
import { HomeContainer, MainContent, FloatingWhatsAppButton } from "./styles";
import { CartaoDoApp } from "../../../push/CartaoDoApp";

interface Activity {
  id: string;
  type: "success" | "info" | "warning" | "error";
  title: string;
  time: string;
}

interface DashboardData {
  profileName?: string;
  primaryCategory?: string;
  cityState?: string;
  analytics?: {
    websiteClicks?: number;
    callClicks?: number;
    requestedRoutes?: number;
    viewsInMap?: number;
  };
}

/* Prancheta Home (cliente): main 32/40, KPIs em cartão dividido, blocos 7/5. */
const Pagina = styled.div`
  padding: 32px 40px 48px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  font-family: var(--font-sans);
  color: #1f1f1f;
  /* Até 1024px o menu vira o botão flutuante do canto: o topo abre espaço para ele. */
  @media (max-width: 1024px) {
    padding-top: 72px;
  }
  @media (max-width: 768px) {
    padding: 72px 16px 40px;
  }
`;

const H1 = styled.h1`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 34px;
  line-height: 40px;
  font-weight: 500;
  letter-spacing: -0.5px;
  overflow-wrap: anywhere;
`;

const Cartao = styled.section`
  background: #fff;
  border: 1px solid #e2e6ea;
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  box-sizing: border-box;
`;

const H3 = styled.span`
  font-family: var(--font-sans);
  font-size: 17px;
  font-weight: 600;
`;

const Kpis = styled.div`
  background: #fff;
  border: 1px solid #e2e6ea;
  border-radius: 16px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
  .kpi {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 18px 22px;
    border-right: 1px solid #eef1f4;
    min-width: 0;
  }
  .kpi:last-child {
    border-right: 0;
  }
  .kpi b {
    font-family: var(--font-sans);
    font-size: 30px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    .kpi {
      border-bottom: 1px solid #eef1f4;
    }
  }
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
  gap: 20px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const LinhaDePalavra = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 90px 120px;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eef1f4;
  font-size: 14px;
  &:last-child {
    border-bottom: 0;
  }
  /* Celular: a palavra ocupa a linha inteira; posição e variação embaixo. */
  @media (max-width: 599.95px) {
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 6px 10px;
    & > :first-child {
      grid-column: 1 / -1;
      white-space: normal !important;
      overflow-wrap: anywhere;
    }
    & > :nth-child(3) {
      justify-self: start;
    }
  }
`;

const Posicao = styled.span<{ $tom: "verde" | "amarelo" | "cinza" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 600;
  background: ${(p) => (p.$tom === "verde" ? "#EAF7EE" : p.$tom === "amarelo" ? "#FFF7E0" : "#F3F6FA")};
  color: ${(p) => (p.$tom === "verde" ? "#137333" : p.$tom === "amarelo" ? "#B06000" : "#5F6368")};
`;

const Acao = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eef1f4;
  font-size: 14px;
  &:last-of-type {
    border-bottom: 0;
  }
  .ic {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ic .material-icons {
    font-size: 18px;
  }
`;

const Saude = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #2b2d31;
  .material-icons {
    font-size: 18px;
  }
`;

const CORES_DA_ATIVIDADE: Record<Activity["type"], { fundo: string; cor: string; icone: string }> = {
  success: { fundo: "#EAF7EE", cor: "#137333", icone: "check_circle" },
  info: { fundo: "#EAF1FB", cor: "#0B4FA8", icone: "info" },
  warning: { fundo: "#FFF7E0", cor: "#B06000", icone: "warning" },
  error: { fundo: "#FCE8E6", cor: "#B3261E", icone: "error" },
};

const formatarData = (iso: string) => {
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? iso : data.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

const posicaoTexto = (pos: number) => (pos > 0 && pos <= 20 ? `${pos}º` : pos > 20 ? "20+" : "—");

export const Home: React.FC = () => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen: isEditModalOpen, closeModal, openModal: abrirEditor } = useEditProfileModal();
  // Cliente com acesso só de visualização não abre o editor (o backend recusaria cada gravação).
  const podeEditar = canEditSelectedProfile(user);
  const openModal = () => {
    if (podeEditar) abrirEditor();
  };
  const [activities, setActivities] = useState<Activity[]>([]);
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [palavras, setPalavras] = useState<KeywordResponse[]>([]);
  const [avaliacoesPendentes, setAvaliacoesPendentes] = useState<number | null>(null);

  const profileId = profileIdDoUsuario(user?.businessProfiles?.[0]);
  const nomeDoPerfil = dashboardData?.profileName || profileData?.name || user?.name || "";
  const primeiroNome = (user?.name || "").split(" ")[0] || "Usuário";

  useEffect(() => {
    const paymentParam = searchParams.get("payment");
    if (paymentParam === "success") {
      setShowPaymentModal(true);
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const activitiesData = await homeService.getActivities();
        setActivities(activitiesData);

        if (user && profileId) {
          const dashData = await homeService.getProfileDashboard(profileId);
          if (dashData) setDashboardData(dashData);

          // Detalhes completos (endereço, descrição, horários) para a completude e o editor.
          try {
            const completo = await profilesTrackingService.getProfileBusinessDetails(profileId);
            setProfileData({ ...(completo as unknown as Profile), id: profileId });
          } catch {
            const profileDetails = await homeService.getProfileDetails(profileId);
            setProfileData(
              (profileDetails as Profile) || {
                id: profileId,
                name: user.name || "",
                category: "Cliente",
                status: "active" as const,
                stats: { views: 0, clicks: 0, ranking: 0 },
              },
            );
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };

    fetchData();
  }, [user, profileId]);

  // Palavras-chave e avaliações pendentes: opcionais, não travam a página.
  useEffect(() => {
    if (!profileId) return;
    let ativo = true;
    ReportsService.getKeywords(profileId)
      .then((lista) => ativo && setPalavras(lista))
      .catch(() => undefined);
    reviewsService
      .list(profileId)
      .then((lista) => ativo && setAvaliacoesPendentes(lista.filter((r) => !r.response).length))
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, [profileId]);

  const handleSaveProfile = async (updatedProfile: Profile) => {
    try {
      await profilesTrackingService.updateProfile(updatedProfile.id, updatedProfile);
      setProfileData(updatedProfile);
      toast.success("Perfil atualizado com sucesso!");
      closeModal();
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      toast.error("Erro ao atualizar perfil");
      throw error;
    }
  };

  const handleWhatsApp = () => {
    const numeroWhatsApp = "+558008001700";
    const mensagem = "Olá! Gostaria de falar com a equipe do GeoRanking.";
    window.open(`https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensagem)}`, "_blank");
  };

  const handlePhoneCall = () => {
    window.location.href = "tel:+558008001700";
  };

  const analytics = dashboardData?.analytics;
  const contatos = (analytics?.callClicks ?? 0) + (analytics?.requestedRoutes ?? 0) + (analytics?.websiteClicks ?? 0);

  const palavrasOrdenadas = useMemo(
    () =>
      [...palavras]
        .filter((p) => !p.isExtra)
        .sort((a, b) => (a.currentRanking || 99) - (b.currentRanking || 99)),
    [palavras],
  );
  const melhores = palavrasOrdenadas.slice(0, 5);
  const ranqueadas = palavrasOrdenadas.filter((p) => p.currentRanking > 0 && p.currentRanking <= 20);
  const posicaoMedia = ranqueadas.length ? ranqueadas.reduce((soma, p) => soma + p.currentRanking, 0) / ranqueadas.length : null;

  const completude = profileData ? itensDeCompletude(profileData) : [];
  const pctCompleto = completude.length ? Math.round((completude.filter((i) => i.ok).length / completude.length) * 100) : null;
  const faltando = completude.find((i) => !i.ok);

  const acaoSugerida =
    avaliacoesPendentes && avaliacoesPendentes > 0
      ? {
          titulo: `Responda ${avaliacoesPendentes} ${avaliacoesPendentes === 1 ? "avaliação que espera" : "avaliações que esperam"} resposta.`,
          texto: "Avaliações sem resposta pesam na decisão de quem está comparando você com o concorrente. A IA já deixou um rascunho; é só revisar e publicar.",
          botao: "Responder agora",
          acao: () => navigate("/reviews"),
        }
      : faltando
        ? {
            titulo: `Complete "${faltando.rotulo.toLowerCase()}" no seu perfil.`,
            texto: "Quanto mais completa a ficha, mais o Google confia nela e mais vezes ela aparece. Leva poucos minutos.",
            botao: "Resolver agora",
            acao: () => openModal(),
          }
        : null;

  return (
    <HomeContainer>
      <Sidebar />
      <MainContent>
        <Pagina>
          <section style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, paddingBottom: 4, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Badge $tom={profileData?.status === "active" || !profileData ? "verde" : "amarelo"}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: profileData?.status === "active" || !profileData ? "#1E8E3E" : "#F9AB00" }} />
                  {profileData?.status === "paused" ? "Perfil pausado" : "Perfil ativo"}
                </Badge>
                <Badge $tom="cinza">
                  <span className="material-icons">sync</span>
                  Sincronizado com o Google Business Profile
                </Badge>
              </div>
              <H1>{`Olá, ${nomeDoPerfil || primeiroNome}`}</H1>
              <p style={{ margin: 0, fontSize: 15, lineHeight: "22px", color: "#5F6368", maxWidth: 640 }}>
                Seu Perfil da Empresa está sincronizado e sendo acompanhado. Abaixo, o que mudou nos últimos 30 dias e o que a GeoRanking fez por você.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Botao type="button" $variante="outlined" onClick={() => navigate(profileId ? `/dashboard/${profileId}` : "/select-profile")}>
                <span className="material-icons">monitoring</span>
                Ver desempenho
              </Botao>
              {podeEditar && (
                <Botao type="button" $variante="filled" onClick={() => openModal()}>
                  <span className="material-icons">edit_square</span>
                  Gerenciar perfil
                </Botao>
              )}
            </div>
          </section>

          <CartaoDoApp />

          <Kpis>
            <div className="kpi">
              <Hint>Visualizações no Maps e na Busca</Hint>
              <b>{(analytics?.viewsInMap ?? 0).toLocaleString("pt-BR")}</b>
              <Hint>últimos 30 dias</Hint>
            </div>
            <div className="kpi">
              <Hint>Cliques no site</Hint>
              <b>{(analytics?.websiteClicks ?? 0).toLocaleString("pt-BR")}</b>
              <Hint>últimos 30 dias</Hint>
            </div>
            <div className="kpi">
              <Hint>Ligações</Hint>
              <b>{(analytics?.callClicks ?? 0).toLocaleString("pt-BR")}</b>
              <Hint>últimos 30 dias</Hint>
            </div>
            <div className="kpi">
              <Hint>Rotas solicitadas</Hint>
              <b>{(analytics?.requestedRoutes ?? 0).toLocaleString("pt-BR")}</b>
              <Hint>últimos 30 dias</Hint>
            </div>
          </Kpis>

          <Grade>
            <Cartao style={{ gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <H3>Como você está aparecendo</H3>
                {palavrasOrdenadas.length > 0 && (
                  <BotaoPequeno type="button" $variante="text" style={{ color: "#1264C7" }} onClick={() => navigate(`/reports/keywords/${profileId}`)}>
                    {`Ver as ${palavrasOrdenadas.length} palavras`}
                  </BotaoPequeno>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {melhores.map((palavra) => {
                  const variacao = palavra.lastRanking && palavra.currentRanking ? palavra.lastRanking - palavra.currentRanking : 0;
                  const tom = palavra.currentRanking > 0 && palavra.currentRanking <= 3 ? "verde" : palavra.currentRanking > 0 && palavra.currentRanking <= 10 ? "amarelo" : "cinza";
                  return (
                    <LinhaDePalavra key={palavra.id}>
                      <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{palavra.title}</span>
                      <Posicao $tom={tom}>{posicaoTexto(palavra.currentRanking)}</Posicao>
                      {variacao !== 0 ? (
                        <Badge $tom={variacao > 0 ? "verde" : "amarelo"}>
                          <span className="material-icons">{variacao > 0 ? "arrow_upward" : "arrow_downward"}</span>
                          {`${Math.abs(variacao)} ${Math.abs(variacao) === 1 ? "posição" : "posições"}`}
                        </Badge>
                      ) : (
                        <Badge $tom="cinza">
                          <span className="material-icons">remove</span>
                          estável
                        </Badge>
                      )}
                    </LinhaDePalavra>
                  );
                })}
                {melhores.length === 0 && <Hint>As posições das suas palavras-chave aparecem aqui depois da primeira medição do Radar.</Hint>}
              </div>
              {posicaoMedia !== null && (
                <Hint>
                  {`Posição média das ${ranqueadas.length} palavras ranqueadas: `}
                  <b style={{ color: "#137333", fontWeight: 500 }}>{`${posicaoMedia.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}º`}</b>
                  {" · medido pelo Radar GeoRanking."}
                </Hint>
              )}
            </Cartao>

            <Cartao style={{ background: "#EAF1FB", borderColor: "#EAF1FB" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="material-icons" style={{ color: "#0B4FA8" }}>bolt</span>
                <H3 style={{ color: "#0B4FA8" }}>{acaoSugerida ? "1 ação pode melhorar seu perfil" : "Seu perfil está em dia"}</H3>
              </div>
              {acaoSugerida ? (
                <>
                  <span style={{ fontFamily: "var(--font-sans)", fontSize: 18, fontWeight: 500, lineHeight: "24px" }}>{acaoSugerida.titulo}</span>
                  <span style={{ fontSize: 14, lineHeight: "20px", color: "#2B2D31" }}>{acaoSugerida.texto}</span>
                  <div style={{ display: "flex", gap: 8, paddingTop: 4, flexWrap: "wrap" }}>
                    {podeEditar && (
                      <>
                        <Botao type="button" $variante="filled" onClick={acaoSugerida.acao}>
                          {acaoSugerida.botao}
                        </Botao>
                        <Botao type="button" $variante="tonal" style={{ background: "#fff" }} onClick={() => openModal()}>
                          Abrir o perfil
                        </Botao>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <span style={{ fontSize: 14, lineHeight: "20px", color: "#2B2D31" }}>Nada pendente por enquanto. Continuamos acompanhando as posições e as avaliações todos os dias.</span>
              )}
              <div style={{ height: 1, background: "#D6E4F7", margin: "6px 0" }} />
              <Hint style={{ color: "#0B4FA8" }}>Saúde do perfil</Hint>
              <Saude>
                <span className="material-icons" style={{ color: "#1E8E3E" }}>check_circle</span>
                Conta Google conectada · sincroniza a cada 24 h
              </Saude>
              <Saude>
                <span className="material-icons" style={{ color: pctCompleto !== null && pctCompleto >= 80 ? "#1E8E3E" : "#F9AB00" }}>{pctCompleto !== null && pctCompleto >= 80 ? "check_circle" : "warning"}</span>
                {pctCompleto !== null ? `Perfil ${pctCompleto}% completo` : "Calculando a completude do perfil…"}
              </Saude>
              <Saude>
                <span className="material-icons" style={{ color: avaliacoesPendentes ? "#F9AB00" : "#1E8E3E" }}>{avaliacoesPendentes ? "warning" : "check_circle"}</span>
                {avaliacoesPendentes === null
                  ? "Conferindo as avaliações…"
                  : avaliacoesPendentes > 0
                    ? `${avaliacoesPendentes} ${avaliacoesPendentes === 1 ? "avaliação aguardando" : "avaliações aguardando"} resposta`
                    : "Todas as avaliações respondidas"}
              </Saude>
            </Cartao>
          </Grade>

          <Grade>
            <Cartao style={{ gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 6 }}>
                <H3>O que fizemos por você</H3>
                <Badge $tom="cinza">últimos 30 dias</Badge>
              </div>
              {activities.slice(0, 6).map((activity) => {
                const cor = CORES_DA_ATIVIDADE[activity.type] ?? CORES_DA_ATIVIDADE.info;
                return (
                  <Acao key={activity.id}>
                    <span className="ic" style={{ background: cor.fundo, color: cor.cor }}>
                      <span className="material-icons">{cor.icone}</span>
                    </span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 500 }}>{activity.title}</span>
                    </div>
                    <Hint style={{ whiteSpace: "nowrap" }}>{formatarData(activity.time)}</Hint>
                  </Acao>
                );
              })}
              {activities.length === 0 && (
                <div style={{ textAlign: "center", padding: "28px 16px" }}>
                  <span className="material-icons" style={{ fontSize: 40, color: "#E2E6EA" }}>inbox</span>
                  <Hint style={{ display: "block", marginTop: 8 }}>Nenhuma atividade registrada ainda. As ações do Radar e da IA aparecem aqui.</Hint>
                </div>
              )}
              <BotaoPequeno type="button" $variante="tonal" style={{ alignSelf: "flex-start", marginTop: 10 }} onClick={() => navigate(profileId ? `/dashboard/${profileId}` : "/select-profile")}>
                Ver todas as ações
                <span className="material-icons">arrow_forward</span>
              </BotaoPequeno>
            </Cartao>

            <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
              <Cartao>
                <H3>Resultado do período</H3>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "var(--font-sans)", fontSize: 36, fontWeight: 500, letterSpacing: "-1px" }}>{contatos.toLocaleString("pt-BR")}</span>
                  <span style={{ fontSize: 14, color: "#2B2D31" }}>contatos gerados pela ficha</span>
                </div>
                <Hint>ligações + cliques no site + rotas · últimos 30 dias</Hint>
                {posicaoMedia !== null && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 4, flexWrap: "wrap" }}>
                    <Posicao $tom="verde" style={{ height: 36, fontSize: 16 }}>{`${posicaoMedia.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}º`}</Posicao>
                    <Hint>posição média hoje nas palavras ranqueadas</Hint>
                  </div>
                )}
              </Cartao>
              <Cartao style={{ flexDirection: "row", alignItems: "center", gap: 16, background: "#F7F9FC", padding: "20px 22px", flexWrap: "wrap" }}>
                <span className="material-icons" style={{ width: 44, height: 44, borderRadius: 12, background: "#EAF1FB", color: "#0B4FA8", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>support_agent</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500 }}>Precisa de ajuda?</span>
                  <Hint>Seg. a sex., 8h às 18h</Hint>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <BotaoPequeno type="button" $variante="outlined" onClick={handlePhoneCall}>
                    <span className="material-icons">call</span>
                    0800 800 1700
                  </BotaoPequeno>
                  <BotaoPequeno type="button" $variante="tonal" onClick={handleWhatsApp}>
                    <span className="material-icons">chat</span>
                    WhatsApp
                  </BotaoPequeno>
                </div>
              </Cartao>
            </div>
          </Grade>
        </Pagina>
      </MainContent>

      <EditProfileModal
        isOpen={isEditModalOpen}
        profile={profileData}
        onClose={closeModal}
        onSave={handleSaveProfile}
        hideIATab={true}
      />

      <FloatingWhatsAppButton onClick={handleWhatsApp} title="Entrar em contato via WhatsApp" aria-label="Falar no WhatsApp">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </FloatingWhatsAppButton>

      <PaymentSuccessModal open={showPaymentModal} onClose={() => setShowPaymentModal(false)} />
    </HomeContainer>
  );
};
